/**
 * The portfolio's load, by the cluster that carries it. READ-ONLY.
 *
 * ⚠️ ONE MOUNT POINT, AND IT IS THE CLUSTER VIEW (Theresa, Oct 19 2026): "if someone needs that
 * level of detail they can go to the cluster lead sub tab". These cards were the Company View's
 * collapsed §03 from Sep 25, which was already an admission that a stakeholder opening that page
 * wants the company answer rather than the split. They now open the Cluster View instead — the
 * question its readers arrive with — beside the check on whether the interviewing is following
 * those ratings, which only means anything next to them.
 *
 * The file keeps its `rolePrioritisation/` home on purpose: the cards are the prioritisation
 * model's own arithmetic, read through the one provider, and moving them under `checkin/` would
 * file them with the Google-Sheet half of that tab, which they have nothing to do with.
 *
 * ⚠️ NO CONTROLS HERE, deliberately (Theresa, 10 Sep 2026). This section used to carry an intern-pool
 * bar above the cards and an Interns / Out-of-office pair inside each card footer, which made the
 * section that exists to be READ at a glance the busiest thing on the page — and put a control for
 * an intern nobody had on screen whether or not one existed. Every capacity what-if now lives in
 * section 03, and the intern controls render only when the team actually holds an intern.
 *
 * One company-wide utilisation figure cannot be acted on: the hours sit with three separate
 * recruiter groups, and on the seeded portfolio 94% resolves into one cluster over its ceiling while
 * the other two hold spare. The card anatomy is deliberately the Check-In tab's — coloured top
 * rule, name left, big figure right, metric rows, controls at the foot — so the two views read as
 * one product.
 *
 * The three bars share ONE scale so their lengths compare directly; each carries its own capacity
 * marker, which is why the markers sit at different points.
 */
import { cn } from '../../../../../shared';
import {
  RATINGS,
  type CapacityAssumptions,
  type EffortModel,
  type LoadStatus,
  type PrioritisationState,
} from '../../../types/rolePrioritisation';
import {
  absorbable,
  clusterFte,
  fteLabel,
  headsIn,
  hoursLabel,
  pctLabel,
  tierHours,
  unplannedIn,
  type ClusterStats,
} from '../../../utils/rolePrioritisation';
import {
  AbsorbList,
  Card,
  Fact,
  FactStrip,
  FactValue,
  RATING_BG,
  RatingBadge,
  StackedBar,
  hoursText,
} from './prioritisationUi';

/**
 * One denominator for all three bars, so their lengths compare directly — the whole point of
 * putting the clusters side by side. Derived from the data rather than fixed: a hardcoded 72 was
 * the seeded Growth capacity, and any bigger cluster would have run off the end of its own bar.
 */
function barScale(stats: ClusterStats[]): number {
  return Math.max(...stats.map((c) => Math.max(c.demand, c.committed)), 1);
}

const TOP_RULE: Record<LoadStatus, string> = {
  headroom: 'border-t-green',
  limit: 'border-t-orange',
  over: 'border-t-red',
};

export interface ClusterLoadProps {
  stats: ClusterStats[];
  state: PrioritisationState;
  model: EffortModel;
  cap: CapacityAssumptions;
  /** Open roles in this cluster with no rating — counted in its role count, priced at zero. */
  unratedByCluster: Record<string, number>;
}

/** The cards and the summary strip, with no section chrome of their own. */
export function ClusterLoad({ stats, state, model, cap, unratedByCluster }: ClusterLoadProps) {
  const overs = stats.filter((c) => c.spare < -0.005);
  const spareElsewhere = stats.reduce((s, c) => s + Math.max(c.spare, 0), 0);

  return (
    <>
      <div className="grid grid-cols-3 gap-2.5 max-[660px]:grid-cols-1">
        {stats.map((c) => (
          <ClusterCard
            key={c.key}
            scale={barScale(stats)}
            c={c}
            state={state}
            model={model}
            cap={cap}
            unrated={unratedByCluster[c.key] ?? 0}
          />
        ))}
      </div>

      <Card className="mt-2.5">
        <FactStrip tone={overs.length ? 'red' : 'blue'}>
          {stats.map((c) => (
            <Fact key={c.key}>
              {c.key} <FactValue>{pctLabel(c.utilisation)}</FactValue> ·{' '}
              {c.spare < -0.005
                ? `over by ${hoursText(-c.spare)}`
                : `${hoursText(c.spare)} spare`}
            </Fact>
          ))}
          <Fact>
            {overs.length ? (
              <>
                Spare hours elsewhere <FactValue>{hoursText(spareElsewhere)}</FactValue>
                {' · Balance the load is where it gets moved'}
              </>
            ) : (
              'Every cluster within its capacity'
            )}
          </Fact>
        </FactStrip>
      </Card>
    </>
  );
}

function ClusterCard({
  c,
  scale,
  state,
  model,
  cap,
  unrated,
}: {
  c: ClusterStats;
  /** Shared across all three cards — see barScale. */
  scale: number;
  state: PrioritisationState;
  model: EffortModel;
  cap: CapacityAssumptions;
  unrated: number;
}) {
  const over = c.spare < -0.005;
  const heads = headsIn(state.team, c.key);
  const notPlanned = unplannedIn(state.team, c.key);
  const loads = RATINGS.map((r) => ({ rating: r, hours: tierHours(model, r) * c.roles[r] }));
  const maxLoad = Math.max(...loads.map((l) => l.hours), 1);

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-3 rounded-lg border-t-[3px] border-solid bg-white p-3.5 shadow-card',
        TOP_RULE[c.status]
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-black">{c.key}</div>
          <div className="mt-0.5 text-xs tabular-nums text-iron">
            {c.roleCount + unrated} roles · {fteLabel(clusterFte(state.team, c.key, cap))} FTE
            {unrated ? <span className="text-orange"> · {unrated} unrated</span> : null}
          </div>
        </div>
        <div className="flex-none pl-2 text-right">
          <div
            data-testid={`cluster-util-${c.key}`}
            className="text-2xl font-semibold leading-7 tabular-nums text-black"
          >
            {pctLabel(c.utilisation)}
          </div>
          <div className="whitespace-nowrap text-[11px] leading-4 text-steel">of capacity</div>
        </div>
      </div>

      {/*
        No marker LABEL on the cluster bars: at card width the blue figure overlapped the "of
        capacity" caption beside it. The footer states Capacity outright, so the marker line alone
        is enough here — the label stays on the wide section-01 bar, where there is room for it.
      */}
      <StackedBar
        segments={loads}
        committed={c.committed}
        scale={scale}
        slim
        ariaLabel={`${c.key}: ${hoursText(c.demand)} of demand against ${hoursText(c.committed)} of committed capacity`}
      />

      <div>
        {loads.map((l) => (
          <div
            key={l.rating}
            className="mt-1 grid grid-cols-[38px_20px_minmax(0,1fr)_34px] items-center gap-1.5 text-xs tabular-nums first:mt-0"
          >
            <RatingBadge rating={l.rating} />
            <span
              className={cn(
                'text-right',
                c.roles[l.rating] ? 'font-semibold text-black' : 'text-steel'
              )}
            >
              {c.roles[l.rating] || '—'}
            </span>
            <span className="h-1.5 overflow-hidden rounded-full bg-cotton">
              <i
                className={cn('block h-full rounded-full', RATING_BG[l.rating])}
                style={{ width: `${(l.hours / maxLoad) * 100}%` }}
              />
            </span>
            <span className="text-right text-iron">{l.hours ? hoursLabel(l.hours) : ''}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-1 border-t border-solid border-pearl pt-2.5 text-xs tabular-nums text-iron">
        <Row k="Demand" v={<b className="font-semibold text-asphalt">{hoursText(c.demand)}</b>} />
        <Row
          k="Capacity"
          v={
            <span>
              <b className="font-semibold text-asphalt">{hoursText(c.committed)}</b>
              {c.lent > 0.005 ? (
                <span className="ml-1.5 font-medium text-trustedBlue">
                  −{hoursText(c.lent)} lent
                </span>
              ) : null}
              {c.borrowed > 0.005 ? (
                <span className="ml-1.5 font-medium text-trustedBlue">
                  +{hoursText(c.borrowed)} in
                </span>
              ) : null}
            </span>
          }
        />
        <Row
          k={over ? 'Over by' : 'Spare'}
          v={
            <b className="font-semibold text-asphalt">{hoursText(over ? -c.spare : c.spare)}</b>
          }
        />
        <Row
          k="Can absorb"
          v={
            over ? (
              <span className="text-steel">over by {hoursText(-c.spare)}</span>
            ) : (
              <AbsorbList entries={absorbable(c.spare, model)} />
            )
          }
        />
        <Row
          k="Recruiters"
          v={
            <span>
              <b className="font-semibold text-asphalt">{heads.length}</b>
              {c.internHours > 0.005 ? ` + ${fteLabel(state.interns[c.key] ?? 0)} intern` : ''}
              {notPlanned.length ? (
                <span className="text-steel"> · {notPlanned.length} not planned</span>
              ) : null}
            </span>
          }
        />
      </div>

    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-steel">{k}</span>
      <span>{v}</span>
    </div>
  );
}
